import React, { useContext, useEffect, useMemo, useState } from 'react'

import { AnalyticsService, initAnalytics } from 'analytics/performance'

export enum CookieType {
  'Necessary' = 'necessary',
  'Performance' = 'performance',
  'Marketing' = 'marketing',
  'Functional' = 'functional',
}

export enum MarketingService {
  'Facebook' = 'facebook',
  'LinkedIn' = 'linkedin',
  'XTwitter' = 'x-twitter',
  'Match2One' = 'match2one',
  'Segment' = 'segment',
}

export const ALL_COOKIES = [
  CookieType.Necessary,
  CookieType.Performance,
  CookieType.Marketing,
  CookieType.Functional,
]

export const ALL_MARKETING_SERVICES = [
  MarketingService.Facebook,
  MarketingService.LinkedIn,
  MarketingService.XTwitter,
  MarketingService.Match2One,
  MarketingService.Segment,
]

const DEFAULT_COOKIE_CONSENT_NAME = 'cookieConsent'

export interface CookieContextType {
  showCookieConsent: boolean | undefined
  allowedCookies: CookieType[]
  setAllowedCookies: (allowed: CookieType[]) => void
  enabledServices: {
    performance: AnalyticsService[]
    marketing: MarketingService[]
  }
}

export const CookieContext = React.createContext<CookieContextType>({
  showCookieConsent: undefined,
  allowedCookies: [],
  setAllowedCookies: (_allowed: CookieType[]) => {},
  enabledServices: {
    performance: [],
    marketing: [],
  },
})

interface CookieContextProviderProps {
  cookieName?: string
  enabledAnalytics?: AnalyticsService[]
  enabledMarketing?: MarketingService[]
}

/* CookieContextProvider should be added to all sub-sites (admin, careers, candidates etc.), which use cookies. Accepted cookie consent is saved to localstorage with key=cookieName (defaults to: cookieConsent)
 * {cookieName}: can be used to define different cookie consent on some/all sub-sites. E.g. in case the user has accepted the cookie consent on admin-site, but we still want the user to accept separate cookie consent on careers-site.
 * {enabledAnalytics}: in case we don't want to enable all analytics on all sub-sites, we can enable them separately.
 * {enabledMarketing}: in case we don't want to enable all marketing pixels on all sub-sites, we can enable them separately.
 * */

export const CookieContextProvider: React.FC<CookieContextProviderProps> = ({
  cookieName = DEFAULT_COOKIE_CONSENT_NAME,
  enabledAnalytics = [],
  enabledMarketing = [],
  children,
}) => {
  // Cookie consent shows up when allowed cookies = [].
  // Allowed cookies = undefined, when previously allowed cookies are not
  // yet fetched from local storage. (We don't want to show the cookie
  // consent modal, before we know if the user has allowed cookies).
  const [allowedCookies, setAllowedCookies] = useState<
    CookieType[] | undefined
  >(undefined)

  const [enabledPerformanceServices, setEnabledPerformanceServices] = useState<
    AnalyticsService[]
  >([])
  const [enabledMarketingServices, setEnabledMarketingServices] = useState<
    MarketingService[]
  >([])

  const onAllowCookies = (allowed: CookieType[]) => {
    // save allowed cookies to localstorage
    localStorage.setItem(cookieName, JSON.stringify(allowed))
    setAllowedCookies(allowed)

    // if analytics cookies were allowed, init all analytics (amplitude etc.)
    if (allowed.includes(CookieType.Performance)) {
      initAnalytics(enabledAnalytics)
    }
  }

  // on mount: we get previously allowed cookies from local storage
  useEffect(() => {
    setEnabledPerformanceServices(enabledAnalytics)
    setEnabledMarketingServices(enabledMarketing)

    const previouslyAllowed = localStorage.getItem(cookieName)
    if (previouslyAllowed) {
      const allowedJSON = JSON.parse(previouslyAllowed)
      onAllowCookies(allowedJSON)
      // if cookies were not allowed yet, cookie consent is shown
    } else setAllowedCookies([])
  }, [])

  const contextValue = useMemo(
    () => ({
      showCookieConsent:
        allowedCookies && !allowedCookies.includes(CookieType.Necessary),
      allowedCookies: allowedCookies || [],
      setAllowedCookies: onAllowCookies,
      enabledServices: {
        performance: enabledPerformanceServices,
        marketing: enabledMarketingServices,
      },
    }),
    [allowedCookies]
  )

  return (
    <CookieContext.Provider value={contextValue}>
      {allowedCookies !== undefined && children}
    </CookieContext.Provider>
  )
}

export const useCookieContext = () => useContext(CookieContext)
